import { useEffect, useMemo, useState } from 'react';
import { DecimalTextInput } from '@/components/common/DecimalTextInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Wallet as WalletIcon,
  Check,
  UserPlus,
  PackageSearch,
} from 'lucide-react';
import {
  calculateProLineTotals,
  computeAmountToPay,
  computeDeliveryFee,
  clampWalletUse,
  DEFAULT_DELIVERY_SETTINGS,
} from '@erp/domain/pro';
import { useDataService } from '@/lib/service-context';
import { useCart } from '@/lib/cart-context';
import { useProPrefs } from '@/lib/user-prefs';
import { CLIENT_MODE_ROUTES, useClientMode, useModePath } from '@/lib/client-mode';
import { useProSettings } from '@/hooks/use-pro-settings';
import { formatTND } from '@/lib/format';
import { proErrorMessage } from '@/lib/pro-error';
import { CitySelect } from '@/components/common/CitySelect';
import type { CreateProClientInput } from '@erp/data-access/interfaces';

type Mode = 'devis' | 'commande';

export function CheckoutPage() {
  const { t } = useTranslation();
  const ds = useDataService();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { items, totals, tier, clear, commissionRate, requests, hasRequests } =
    useCart();
  // Même règle d'affichage que le panier : visible par défaut, masquable
  // depuis Paramètres → Options, seulement si le compte a un taux > 0.
  const { showCommission } = useProPrefs();
  const clientMode = useClientMode();
  const modePath = useModePath();
  const commissionVisible = !clientMode && showCommission && commissionRate > 0;
  const proSettings = useProSettings();

  // Un panier qui contient un produit à chiffrer ne peut pas être commandé :
  // il manque un prix. Le mode est alors FORCÉ à « devis » — y compris quand
  // l'URL dit `?mode=commande` (retour arrière après avoir ajouté une demande).
  const initialMode: Mode = (searchParams.get('mode') as Mode) || 'commande';
  const [modeChoice, setMode] = useState<Mode>(initialMode);
  const mode: Mode = hasRequests ? 'devis' : modeChoice;
  const [proClientId, setProClientId] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [walletUseRequested, setWalletUseRequested] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryPostalCode, setDeliveryPostalCode] = useState('');
  const [deliveryComplement, setDeliveryComplement] = useState('');
  const [error, setError] = useState<string | null>(null);

  const clientsQuery = useQuery({
    queryKey: ['pro-clients'],
    queryFn: () => ds.proClients.list(),
  });
  const balanceQuery = useQuery({
    queryKey: ['pro-wallet-balance'],
    queryFn: () => ds.proWallet.getBalance(),
  });

  const buildClientInput = (): CreateProClientInput | null => {
    const prenom = firstName.trim();
    const nom = lastName.trim();
    const telephone = phone.trim();
    if (!prenom && !nom && !telephone) return null;

    return {
      nom: nom || prenom || telephone,
      prenom: nom && prenom ? prenom : undefined,
      telephone: telephone || undefined,
      adresse: deliveryAddress.trim() || undefined,
      ville: deliveryCity.trim() || undefined,
      cp: deliveryPostalCode.trim() || undefined,
      complement: deliveryComplement.trim() || undefined,
      notes: t(
        'pro.checkout.auto_client_note',
        'Ajouté depuis le checkout Pro',
      ),
    };
  };

  const willCreateClient = !proClientId && buildClientInput() !== null;

  // pre-fill from selected client
  useEffect(() => {
    if (!proClientId) return;
    const c = (clientsQuery.data ?? []).find((x) => x.id === proClientId);
    if (!c) return;
    setFirstName((p) => p || c.prenom || '');
    setLastName((p) => p || c.nom || '');
    setPhone((p) => p || c.telephone || '');
    setDeliveryAddress((p) => p || c.adresse || '');
    setDeliveryCity((p) => p || c.ville || '');
    setDeliveryPostalCode((p) => p || c.cp || '');
    setDeliveryComplement((p) => p || c.complement || '');
  }, [proClientId, clientsQuery.data]);

  const deliverySettings = useMemo(
    () =>
      proSettings.data
        ? { fee: proSettings.data.deliveryFee, freeFrom: proSettings.data.freeDeliveryThreshold || null }
        : DEFAULT_DELIVERY_SETTINGS,
    [proSettings.data],
  );
  const deliveryFee = useMemo(
    () =>
      mode === 'commande'
        ? computeDeliveryFee(totals.pttc, deliverySettings)
        : 0,
    [mode, totals.pttc, deliverySettings],
  );

  const totalBeforeWallet = totals.pttc + deliveryFee;
  const walletUsed = useMemo(() => {
    if (mode !== 'commande' || !useWallet) return 0;
    return clampWalletUse(
      walletUseRequested,
      balanceQuery.data?.balance ?? 0,
      totalBeforeWallet,
    );
  }, [
    mode,
    useWallet,
    walletUseRequested,
    balanceQuery.data?.balance,
    totalBeforeWallet,
  ]);
  const totalAPayer = computeAmountToPay(totals.pttc, deliveryFee, walletUsed);

  const submit = useMutation({
    onMutate: () => setError(null),
    mutationFn: async () => {
      if (items.length === 0 && requests.length === 0)
        throw new Error(t('pro.checkout.empty_cart', 'Le panier est vide'));
      // Les champs marqués * ne sont réellement bloquants que pour une
      // COMMANDE (la livraison exige un destinataire joignable et une
      // adresse). Un devis rapide sans client reste possible.
      if (mode === 'commande') {
        const missing: string[] = [];
        if (!firstName.trim() && !lastName.trim())
          missing.push(t('pro.checkout.last_name', 'Nom'));
        if (!phone.trim())
          missing.push(t('pro.checkout.phone', 'Numéro de téléphone'));
        if (!deliveryAddress.trim())
          missing.push(t('pro.checkout.address', 'Adresse'));
        if (!deliveryPostalCode.trim())
          missing.push(t('pro.checkout.postal_code', 'Code postal'));
        if (!deliveryCity.trim())
          missing.push(t('pro.checkout.city', 'Ville'));
        if (missing.length > 0) {
          throw new Error(
            t('pro.checkout.missing_fields', 'Champs obligatoires manquants : {{fields}}', {
              fields: missing.join(', '),
            }),
          );
        }
      }
      // Don't submit a commande while the tenant delivery/commission settings
      // are still loading — deliveryFee would use the generic fallback instead
      // of the tenant's configured fee (a fast/double click could persist it).
      if (mode === 'commande' && proSettings.isLoading)
        throw new Error(t('pro.checkout.settings_loading', 'Chargement des paramètres… réessayez.'));
      const lines = items.map((it) => {
        const pre = it.quantity * it.unitHt;
        const montantRemise = (pre * it.remisePct) / 100;
        const pht = pre - montantRemise;
        const pttc = pht * (1 + it.vatRate / 100);
        return {
          articleId: it.articleId,
          quantite: it.quantity,
          pht,
          pttc,
          tva: it.vatRate,
          remise: it.remisePct,
          montantRemise,
        };
      });
      const clientInput = proClientId ? null : buildClientInput();
      const linkedProClientId =
        proClientId ||
        (clientInput ? (await ds.proClients.create(clientInput)).id : null);

      if (mode === 'devis') {
        return ds.proDevis.create({
          proClientId: linkedProClientId,
          pht: totals.pht,
          pttc: totals.pttc,
          tva: totals.tva,
          tauxRemise: 0,
          typeRemise: tier,
          totalCommission: totals.totalCommission,
          lines,
          // Les demandes n'entrent dans AUCUN total : elles n'ont pas de prix
          // tant que le commerçant n'a pas répondu.
          productRequests: requests.map((r) => ({
            label: r.label,
            quantity: r.quantity,
            note: r.note,
          })),
        });
      }
      return ds.proCommandes.create({
        proClientId: linkedProClientId,
        pht: totals.pht,
        pttc: totals.pttc,
        tva: totals.tva,
        tauxRemise: 0,
        typeRemise: tier,
        totalCommission: totals.totalCommission,
        deliveryFee,
        totalUsed: walletUsed,
        totalAPayer,
        usedSolde: walletUsed > 0,
        deliveryAddress: deliveryAddress || undefined,
        deliveryCity: deliveryCity || undefined,
        deliveryPostalCode: deliveryPostalCode || undefined,
        deliveryComplement: deliveryComplement || undefined,
        lines,
      });
    },
    onSuccess: (result) => {
      clear();
      qc.invalidateQueries({ queryKey: ['pro-devis'] });
      qc.invalidateQueries({ queryKey: ['pro-commandes'] });
      qc.invalidateQueries({ queryKey: ['pro-clients'] });
      qc.invalidateQueries({ queryKey: ['pro-wallet-balance'] });
      qc.invalidateQueries({ queryKey: ['pro-wallet-movements'] });
      // Les fiches devis/ordre affichent « Votre Commission » : y renvoyer
      // depuis le mode client la mettrait sous les yeux du client. On revient
      // donc à la boutique client, avec le numéro du document en bandeau.
      if (clientMode) {
        navigate(
          `${CLIENT_MODE_ROUTES.shop}?placed=${encodeURIComponent(result.code)}`,
        );
      } else if (mode === 'devis') {
        navigate(`/devis/${encodeURIComponent(result.code)}`);
      } else {
        navigate(`/ordres/${encodeURIComponent(result.code)}`);
      }
    },
    // Les codes bruts des RPC/triggers (line_price_mismatch,
    // delivery_fee_mismatch, …) sont traduits au même endroit que sur la
    // fiche devis.
    onError: (err) => setError(proErrorMessage(err, t)),
  });

  if (items.length === 0 && requests.length === 0) {
    return (
      <div className="pro-card p-12 text-center">
        <p style={{ color: 'var(--pro-text-grey)' }}>
          {t('pro.checkout.empty_cart', 'Le panier est vide')}
        </p>
        <button
          type="button"
          onClick={() => navigate(modePath('/home'))}
          className="mt-3 text-[14px] font-medium hover:underline"
          style={{ color: 'var(--pro-primary)' }}
        >
          {t('pro.checkout.browse', 'Parcourir le catalogue')}
        </button>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[1fr_400px] gap-4 items-start">
      {/* Left form */}
      <div className="pro-card p-6 space-y-6">
        {/* Mode toggle. Verrouillé sur « Devis » dès qu'un produit reste à
            chiffrer : le bouton Commande est rendu inerte plutôt que retiré,
            pour que la raison soit lisible au lieu d'être devinée. */}
        <div className="space-y-2">
          <div
            className="inline-flex p-1 rounded-xl"
            style={{ background: 'var(--pro-grey-subtle)' }}
          >
            <button
              type="button"
              onClick={() => setMode('devis')}
              className="px-4 py-2 rounded-lg text-[13px] font-semibold transition-colors"
              style={
                mode === 'devis'
                  ? {
                      background: 'white',
                      color: 'var(--pro-primary)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    }
                  : { color: 'var(--pro-text-grey)' }
              }
            >
              {t('pro.checkout.mode_devis', 'Devis')}
            </button>
            <button
              type="button"
              onClick={() => setMode('commande')}
              disabled={hasRequests}
              title={
                hasRequests
                  ? t(
                      'pro.request.devis_only_short',
                      'Impossible : un produit demandé reste à chiffrer.',
                    )
                  : undefined
              }
              className="px-4 py-2 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={
                mode === 'commande'
                  ? {
                      background: 'white',
                      color: 'var(--pro-primary)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    }
                  : { color: 'var(--pro-text-grey)' }
              }
            >
              {t('pro.checkout.mode_commande', 'Commande')}
            </button>
          </div>
          {hasRequests && (
            <p
              className="text-[12px] leading-relaxed"
              style={{ color: 'var(--pro-primary-text)' }}
            >
              {t(
                'pro.request.devis_only',
                'Votre panier contient un produit à chiffrer : il part en devis. Vous recevrez une réponse pour chaque produit demandé.',
              )}
            </p>
          )}
        </div>

        {/* Personal info */}
        <Section
          title={t('pro.checkout.personal_info', 'Information personnelles')}
        >
          <div
            className="rounded-xl p-4 space-y-3"
            style={{ border: '1px solid var(--pro-line)' }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <UserPlus
                  className="w-4 h-4"
                  style={{ color: 'var(--pro-primary)' }}
                />
                <span
                  className="text-[14px] font-semibold"
                  style={{
                    color: 'var(--pro-text-dark)',
                    fontFamily: 'Figtree',
                  }}
                >
                  {t('pro.checkout.client', 'Client (carnet)')}
                </span>
              </div>
              {willCreateClient && (
                <span
                  className="px-2.5 py-1 rounded-full text-[12px] font-semibold"
                  style={{
                    background: 'var(--pro-primary-subtle)',
                    color: 'var(--pro-primary)',
                  }}
                >
                  {t('pro.checkout.client_will_be_saved', 'Ajout automatique')}
                </span>
              )}
            </div>

            {clientsQuery.data && clientsQuery.data.length > 0 ? (
              <select
                value={proClientId}
                onChange={(e) => setProClientId(e.target.value)}
                className="pro-input w-full"
              >
                <option value="">
                  {t('pro.checkout.no_client', '— Nouveau / saisie libre —')}
                </option>
                {clientsQuery.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.prenom ?? ''} ${c.nom}`.trim()}
                    {c.telephone ? ` · ${c.telephone}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div
                className="text-[13px]"
                style={{ color: 'var(--pro-text-grey)' }}
              >
                {t('pro.checkout.no_saved_clients', 'Aucun client enregistré')}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label={t('pro.checkout.first_name', 'Prénom')}
              value={firstName}
              onChange={setFirstName}
              required
              placeholder={t(
                'pro.checkout.first_name_placeholder',
                'Taper votre prénom',
              )}
            />
            <FormField
              label={t('pro.checkout.last_name', 'Nom')}
              value={lastName}
              onChange={setLastName}
              required
              placeholder={t(
                'pro.checkout.last_name_placeholder',
                'Taper votre nom',
              )}
            />
            <FormField
              label={t('pro.checkout.phone', 'Numéro de téléphone')}
              value={phone}
              onChange={setPhone}
              prefix="+216"
              required
              inputMode="tel"
              placeholder={t(
                'pro.checkout.phone_placeholder',
                'Taper votre tel',
              )}
            />
          </div>
        </Section>

        {/* Address */}
        {mode === 'commande' && (
          <Section title={t('pro.checkout.addresses', 'Adresses')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label={t('pro.checkout.address', 'Adresse')}
                value={deliveryAddress}
                onChange={setDeliveryAddress}
                required
                placeholder={t(
                  'pro.checkout.address_placeholder',
                  'Taper votre adresse',
                )}
              />
              <FormField
                label={t('pro.checkout.complement', "Complément d'adresse")}
                value={deliveryComplement}
                onChange={setDeliveryComplement}
                placeholder={t(
                  'pro.checkout.complement_placeholder',
                  "Taper votre complément d'adresse",
                )}
              />
              <FormField
                label={t('pro.checkout.postal_code', 'Code postal')}
                value={deliveryPostalCode}
                onChange={setDeliveryPostalCode}
                required
                inputMode="numeric"
                maxLength={4}
                placeholder={t(
                  'pro.checkout.postal_code_placeholder',
                  'Taper votre code postal',
                )}
              />
              <CitySelect
                label={t('pro.checkout.city', 'Ville')}
                value={deliveryCity}
                onChange={setDeliveryCity}
                required
              />
            </div>
          </Section>
        )}

        {/* Delivery mode */}
        {mode === 'commande' && (
          <Section title={t('pro.checkout.delivery_mode', 'Mode de livraison')}>
            <label
              className="flex items-center gap-4 p-4 rounded-xl cursor-pointer"
              style={{ border: '1px solid var(--pro-line)' }}
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: 'var(--pro-primary)',
                  border: '1px solid var(--pro-primary)',
                }}
              >
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </span>
              <span
                className="flex-1 text-[14px] font-medium"
                style={{ color: 'var(--pro-text-dark)' }}
              >
                {t(
                  'pro.checkout.delivery_tunisia',
                  'Livraison - Toute la Tunisie',
                )}
              </span>
              <span
                className="text-[14px] font-medium"
                style={{ color: 'var(--pro-text-dark)' }}
              >
                {formatTND(deliveryFee)}
              </span>
            </label>
          </Section>
        )}

        {/* Submit */}
        <div className="flex justify-end">
          {error && (
            <div
              className="mr-auto p-3 rounded-xl text-[13px]"
              style={{
                background: 'rgba(223,28,65,0.08)',
                color: 'var(--pro-danger)',
                border: '1px solid rgba(223,28,65,0.2)',
              }}
            >
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={submit.isPending || (mode === 'commande' && proSettings.isLoading)}
            className="h-12 px-8 rounded-lg text-[15px] font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
          >
            {submit.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'devis'
              ? t('pro.checkout.submit_devis', 'Confirmer le devis')
              : t('pro.checkout.submit_commande', 'Finalisez Votre Commande')}
          </button>
        </div>
      </div>

      {/* Right summary */}
      <div className="pro-card p-6 lg:sticky lg:top-4 space-y-4">
        <h3
          className="text-[18px] font-semibold"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {items.length + requests.length} {t('pro.checkout.items', 'articles')}
        </h3>

        <div className="space-y-3">
          {items.map((it) => {
            const unitTtc = calculateProLineTotals({
              quantity: 1,
              unitHt: it.unitHt,
              remisePct: it.remisePct,
              vatRate: it.vatRate,
            }).pttc;
            return (
              <div
                key={it.articleId}
                className="flex gap-3 p-3 rounded-2xl"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div
                  className="w-16 h-16 rounded-xl overflow-hidden shrink-0"
                  style={{ background: 'var(--pro-grey-subtle)' }}
                >
                  {it.imageUrl ? (
                    <img
                      src={it.imageUrl}
                      alt={it.name}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[14px] truncate"
                    style={{
                      color: 'var(--pro-text-dark)',
                      fontFamily: 'Figtree',
                    }}
                  >
                    {it.name}
                  </div>
                  <div
                    className="text-[12px]"
                    style={{ color: 'var(--pro-text-grey)' }}
                  >
                    {it.code}
                  </div>
                  <div
                    className="text-[14px] font-semibold mt-1"
                    style={{ color: 'var(--pro-text-dark)' }}
                  >
                    {it.quantity} × {formatTND(unitTtc)} TTC
                  </div>
                </div>
              </div>
            );
          })}

          {requests.map((request) => (
            <div
              key={request.id}
              className="flex gap-3 p-3 rounded-2xl"
              style={{
                border: '1px dashed var(--pro-primary-light)',
                background: 'var(--pro-primary-subtle)',
              }}
            >
              <div
                className="w-16 h-16 rounded-xl shrink-0 flex items-center justify-center"
                style={{ background: 'white' }}
              >
                <PackageSearch
                  className="w-6 h-6"
                  style={{ color: 'var(--pro-primary)' }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[14px] break-words"
                  style={{
                    color: 'var(--pro-text-dark)',
                    fontFamily: 'Figtree',
                  }}
                >
                  {request.label}
                </div>
                {request.note && (
                  <div
                    className="text-[12px] break-words"
                    style={{ color: 'var(--pro-text-grey)' }}
                  >
                    {request.note}
                  </div>
                )}
                <div
                  className="text-[14px] font-semibold mt-1"
                  style={{ color: 'var(--pro-primary-text)' }}
                >
                  {request.quantity} ×{' '}
                  {t('pro.request.price_pending', 'Prix à confirmer')}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Wallet usage */}
        {mode === 'commande' && (balanceQuery.data?.balance ?? 0) > 0 && (
          <div
            className="space-y-3 pt-4"
            style={{ borderTop: '1px solid var(--pro-line)' }}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useWallet}
                onChange={(e) => setUseWallet(e.target.checked)}
                className="w-4 h-4 accent-orange-500"
              />
              <span
                className="text-[16px] font-semibold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t(
                  'pro.checkout.use_wallet',
                  'Utiliser mon solde portefeuille',
                )}
              </span>
            </label>
            {useWallet && (
              <div
                className="rounded-2xl p-4 space-y-3"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-[14px] font-semibold"
                    style={{ color: 'var(--pro-primary)' }}
                  >
                    {t('pro.checkout.your_balance', 'Votre Solde :')}
                  </span>
                  <span
                    className="text-[14px] font-semibold flex-1"
                    style={{ color: 'var(--pro-primary)' }}
                  >
                    {formatTND(balanceQuery.data!.balance)}
                  </span>
                  <WalletIcon
                    className="w-4 h-4"
                    style={{ color: 'var(--pro-primary)' }}
                  />
                </div>
                <div>
                  <label
                    className="text-[14px] font-medium block mb-1.5"
                    style={{ color: 'var(--pro-text-muted)' }}
                  >
                    {t('pro.checkout.amount_to_use', 'Montant à utiliser')}
                  </label>
                  <div
                    className="flex items-center px-3 h-10 rounded-lg"
                    style={{ border: '1px solid var(--pro-border)' }}
                  >
                    <DecimalTextInput
                      maxDecimals={3}
                      value={walletUseRequested}
                      onValueChange={(n) =>
                        setWalletUseRequested(Number.isFinite(n) ? n : 0)
                      }
                      className="flex-1 text-[14px] focus:outline-none"
                      style={{ color: 'var(--pro-text-dark)' }}
                    />
                    <span
                      className="text-[14px] ml-2"
                      style={{ color: 'var(--pro-text-grey)' }}
                    >
                      DT
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setWalletUseRequested(
                        Math.min(balanceQuery.data!.balance, totalBeforeWallet),
                      )
                    }
                    className="text-[12px] mt-2"
                    style={{ color: 'var(--pro-text-light)' }}
                  >
                    {t(
                      'pro.checkout.use_full_balance',
                      "Utilisez l'intégralité de votre solde",
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Totals */}
        <div
          className="rounded-2xl p-6 space-y-3"
          style={{
            background: 'var(--pro-primary-subtle)',
            border: '1px solid var(--pro-primary-light)',
          }}
        >
          <Row
            label={t('pro.checkout.subtotal_total', 'Sous - Total')}
            value={formatTND(totals.pttc)}
          />
          {walletUsed > 0 && (
            <Row
              label={t('pro.checkout.reduction', 'Reduction')}
              value={`-${formatTND(walletUsed)}`}
            />
          )}
          {mode === 'commande' && (
            <Row
              label={t('pro.checkout.delivery_label', 'Livraison')}
              value={formatTND(deliveryFee)}
            />
          )}
          <div
            className="h-px"
            style={{ background: 'rgba(255,107,30,0.25)' }}
          />
          <Row
            label={t('pro.checkout.total_due', "Total d'un payeur")}
            value={formatTND(mode === 'devis' ? totals.pttc : totalAPayer)}
            bold
          />
          {/* Le total ne ment pas : il ne couvre que ce qui est chiffré. */}
          {hasRequests && (
            <div
              className="text-[11px] leading-relaxed"
              style={{ color: 'var(--pro-text-grey)' }}
            >
              {t(
                'pro.request.total_excludes',
                'Hors produits demandés — leur prix sera ajouté après réponse.',
              )}
            </div>
          )}
          {commissionVisible && (
            <>
              <div
                className="flex justify-between text-[12px] font-medium"
                style={{ color: 'var(--pro-primary)' }}
              >
                <span>{t('pro.cart.commission', 'Commission estimée')}</span>
                <span>{formatTND(totals.totalCommission)}</span>
              </div>
              {totals.totalCommission > 0 && (
                <div
                  className="text-[11px]"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {t(
                    'pro.cart.commission_pending_hint',
                    'Disponible après validation de la commande.',
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <h2
        className="text-[16px] font-semibold"
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  required,
  prefix,
  placeholder,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  prefix?: string;
  placeholder?: string;
  inputMode?: 'tel' | 'numeric';
  maxLength?: number;
}) {
  return (
    <div>
      <label
        className="text-[14px] font-medium mb-1.5 block"
        style={{ color: 'var(--pro-text-muted)' }}
      >
        {label}{' '}
        {required && <span style={{ color: 'var(--pro-danger)' }}>*</span>}
      </label>
      <div
        className="flex items-stretch h-11 rounded-lg overflow-hidden"
        style={{ border: '1px solid var(--pro-border)', background: 'white' }}
      >
        {prefix && (
          <div
            className="flex items-center gap-1 px-3 text-[14px] font-medium shrink-0"
            style={{ color: 'var(--pro-text-light)' }}
          >
            🇹🇳 {prefix}
          </div>
        )}
        <input
          type="text"
          required={required}
          inputMode={inputMode}
          maxLength={maxLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 h-11 text-[14px] focus:outline-none bg-transparent"
          style={{ color: 'var(--pro-text-dark)' }}
        />
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>
        {label}
      </span>
      <span
        className={`${bold ? 'text-[18px] font-bold' : 'text-[16px] font-medium'}`}
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {value}
      </span>
    </div>
  );
}
